/* eslint-disable complexity */
import cloneDeep from 'lodash/cloneDeep';

import { TReturnType } from '@r1-frontend/service-engineer-visit';

import { speedBonusConnectionAnalytics } from '~/src/entities/speed-bonus/analytics';
import * as actions from '~/src/entities/speed-bonus/context/actions';
import { ESpeedBonusActions } from '~/src/entities/speed-bonus/context/actions/constants';
import { IBusinessLogic } from '~/src/entities/speed-bonus/context/actions/setBusinessLogic';
import { TActionResult } from '~/src/entities/speed-bonus/context/actions/setResult';
import { IPreparedRouter } from '~/src/entities/speed-bonus/context/actions/setRouters';
import { ISpeedBonusTariff } from '~/src/entities/speed-bonus/context/actions/setTariff';
import { InferValueTypes } from '~/src/store';

export type TAction = ReturnType<InferValueTypes<typeof actions>>

export type TInitialState = {
    isLoading: boolean,
    isShowConnection: boolean,
    tariff: ISpeedBonusTariff | null,
    stepIndex: number,
    routers: IPreparedRouter[],
    connectionCost?: number,
    timeSlotData: TReturnType | null,
    result: TActionResult,
    businessLogic?: IBusinessLogic,
}

export const initialState: TInitialState = {
    isLoading: false,
    isShowConnection: false,
    tariff: null,
    stepIndex: 0,
    routers: [],
    timeSlotData: null,
    result: {
        entity: 'none',
        status: 'none',
        header: '',
        message: '',
    },
};

export const reducer = (state: TInitialState = initialState, action: TAction) => {
    switch (action.type) {
        case ESpeedBonusActions.toggleIsLoading:
            return {
                ...state,
                isLoading: action.payload,
            };
        case ESpeedBonusActions.toggleIsShowConnection:
            return {
                ...state,
                isShowConnection: action.payload,
            };
        case ESpeedBonusActions.setBusinessLogic:
            return {
                ...state,
                businessLogic: action.payload,
            };
        case ESpeedBonusActions.setTariff: {
            const { title, connectionFlow, shopItemId, ...rest } = action.payload;

            speedBonusConnectionAnalytics.setTariff({ title, connectionFlow, shopItemId });

            return {
                ...state,
                tariff: { title, connectionFlow, shopItemId, ...rest },
            };
        }
        case ESpeedBonusActions.goToNextStep:
            return {
                ...state,
                stepIndex: state.stepIndex + 1,
            };
        case ESpeedBonusActions.setRouters:
            return {
                ...state,
                routers: action.payload,
            };
        case ESpeedBonusActions.incrementQuantity: {
            const routersClone = cloneDeep(state.routers);
            const currRouter = routersClone.find(el => el.id === action.payload);

            if (currRouter) {
                currRouter.quantity += 1;
            }

            return {
                ...state,
                routers: routersClone,
            };
        }
        case ESpeedBonusActions.decrementQuantity: {
            const routersClone = cloneDeep(state.routers);
            const currRouter = routersClone.find(el => el.id === action.payload);

            if (currRouter) {
                currRouter.quantity -= 1;
            }

            return {
                ...state,
                routers: routersClone,
            };
        }
        case ESpeedBonusActions.setRoutersPrice: {
            const { id, duration } = action.payload;
            const routersClone = cloneDeep(state.routers);
            const currRouter = routersClone.find(el => el.id === id);

            if (currRouter) {
                const newSelectedPrice = currRouter.prices.find(el => el.duration === duration);

                if (newSelectedPrice) {
                    currRouter.selectedPrice = newSelectedPrice;
                }
            }

            return {
                ...state,
                routers: routersClone,
            };
        }
        case ESpeedBonusActions.setConnectionCost:
            return {
                ...state,
                connectionCost: action.payload,
            };
        case ESpeedBonusActions.setTimeSlotData:
            return {
                ...state,
                timeSlotData: action.payload,
            };
        case ESpeedBonusActions.setResult:
            return {
                ...state,
                result: {
                    ...action.payload,
                    target: action.payload.target ?? 'popup',
                },
            };
        case ESpeedBonusActions.flushState:
            return {
                ...initialState,
            };
        default:
            return { ...state };
    }
};
